import { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import { getUrl } from "../../helpers/api/api";
import { pcoAnnouncements_endpoint } from "../../helpers/api/apiConstants";
import { momentFromIso } from "../../helpers/dateHelper";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";
import { announcementsString, noUpcomingAnnouncementsString } from "../../constants";

// announcement.showAt is an ISO string from the backend - the date it
// started (or will start) showing, used here as a rough "posted" date.
const formatShowAt = (showAt) => {
  if (!showAt) return "";
  return momentFromIso(showAt).format("MMM D, YYYY");
};

const Announcements = () => {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const [announcements, setAnnouncements] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(getUrl(pcoAnnouncements_endpoint))
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setAnnouncements(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    fontSize: "min(9vw, 34px)",
    margin: "0 0 24px",
  };

  const cardStyle = {
    border: `1px solid ${theme.palette.brand.border}`,
    borderRadius: "10px",
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
        minHeight: "calc(100vh - var(--nav-height))",
      }}
    >
      <UrgentMessageDisplay page="Announcements" />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "28px 24px 64px" }}>
        <div style={eyebrowStyle}>Church Center</div>
        <h1 style={headingStyle}>{announcementsString(language)}</h1>

        {loaded && announcements.length === 0 && (
          <div
            style={{
              fontFamily: theme.typography.paragraphFontFamily,
              fontSize: "14px",
              color: theme.palette.text.secondary,
              padding: "24px",
              backgroundColor: theme.palette.brand.linen,
              borderRadius: "10px",
            }}
          >
            {noUpcomingAnnouncementsString(language)}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "20px",
          }}
        >
          {announcements.map((announcement) => {
            const CardTag = announcement.link ? "a" : "div";
            const linkProps = announcement.link
              ? { href: announcement.link, target: "_blank", rel: "noreferrer" }
              : {};

            return (
              <CardTag
                key={announcement.id}
                {...linkProps}
                style={{
                  ...cardStyle,
                  textDecoration: "none",
                  cursor: announcement.link ? "pointer" : "default",
                }}
              >
                {/* Only rendered when there's an actual photo, and small
                    when it is - a photo is the exception here, not the
                    point, so it shouldn't dominate the card the way a
                    16:9 hero would when most announcements are text-only. */}
                {announcement.imageUrl && (
                  <div
                    style={{
                      height: "110px",
                      backgroundColor: theme.palette.brand.linen,
                      backgroundImage: `url(${announcement.imageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                )}
                <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                  <div
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {announcement.featured && "Featured · "}
                    {formatShowAt(announcement.showAt)}
                  </div>
                  <div
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "18px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {announcement.title}
                  </div>
                  {announcement.summary && (
                    <p
                      style={{
                        fontFamily: theme.typography.paragraphFontFamily,
                        fontSize: "16px",
                        lineHeight: 1.6,
                        color: theme.palette.text.secondary,
                        margin: "4px 0 0",
                      }}
                    >
                      {announcement.summary}
                    </p>
                  )}
                </div>
              </CardTag>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Announcements;
